import { useState, useEffect, useContext } from 'react';
import { AppContext } from '@/App';
import axios from 'axios';
import { DollarSign, TrendingUp, Users, Calendar, Search, Download, Eye, X } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, BarChart, Bar } from 'recharts';
import { canUserEdit } from '@/utils/permissions';

function SalaryDetails() {
  const { API, user } = useContext(AppContext);
  const [salaries, setSalaries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [selectedYear, setSelectedYear] = useState('2025');
  const [searchTerm, setSearchTerm] = useState('');
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedSalary, setSelectedSalary] = useState(null);

  const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
  const years = ['2023', '2024', '2025'];

  const isAdmin = canUserEdit(user, '/hr/salary');

  useEffect(() => {
    fetchSalaries();
  }, [selectedMonth, selectedYear]);

  const fetchSalaries = async () => {
    try {
      let url = `${API}/hr/salaries`;
      const params = new URLSearchParams();
      if (selectedMonth) params.append('month', selectedMonth);
      if (selectedYear) params.append('year', selectedYear);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setSalaries(response.data);
    } catch (error) {
      console.error('Error fetching salaries:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredSalaries = salaries.filter(sal => 
    sal.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sal.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    sal.employeeId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Calculate summary stats
  const totalGross = filteredSalaries.reduce((sum, sal) => sum + (sal.grossSalary || 0), 0);
  const totalNet = filteredSalaries.reduce((sum, sal) => sum + (sal.netSalary || 0), 0);
  const avgSalary = filteredSalaries.length > 0 ? totalNet / filteredSalaries.length : 0;

  // Chart data
  const departmentWiseData = filteredSalaries.reduce((acc, sal) => {
    const existing = acc.find(d => d.department === sal.department);
    if (existing) {
      existing.total += sal.netSalary || 0;
      existing.count += 1;
    } else {
      acc.push({ department: sal.department, total: sal.netSalary || 0, count: 1 });
    }
    return acc;
  }, []);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1a237e]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="salary-details-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <DollarSign className="text-[#1a237e]" size={28} />
            Master Roll
          </h1>
          <p className="text-gray-600 mt-1">Employee salary records and analytics</p>
        </div>
      </div>

      {/* KPI Cards */}
      {isAdmin && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="bg-gradient-to-br from-[#1a237e] to-[#3949ab] rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Total Gross Salary</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalGross)}</p>
              </div>
              <DollarSign size={32} className="text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-[#e65100] to-[#f57c00] rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Total Net Payout</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(totalNet)}</p>
              </div>
              <TrendingUp size={32} className="text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-600 to-green-500 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Average Salary</p>
                <p className="text-2xl font-bold mt-1">{formatCurrency(avgSalary)}</p>
              </div>
              <Users size={32} className="text-white/30" />
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-600 to-purple-500 rounded-xl p-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-white/70 text-sm">Total Employees</p>
                <p className="text-2xl font-bold mt-1">{filteredSalaries.length}</p>
              </div>
              <Calendar size={32} className="text-white/30" />
            </div>
          </div>
        </div>
      )}

      {/* Department-wise Chart */}
      {isAdmin && departmentWiseData.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Department-wise Salary Distribution</h3>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={departmentWiseData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="department" />
                <YAxis tickFormatter={(val) => `${(val / 100000).toFixed(0)}L`} />
                <Tooltip formatter={(val) => formatCurrency(val)} />
                <Bar dataKey="total" fill="#1a237e" name="Total Salary" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
            <input
              type="text"
              placeholder="Search by name or department..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
              data-testid="salary-search-input"
            />
          </div>
          <select
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
            data-testid="month-filter"
          >
            <option value="">All Months</option>
            {months.map(month => (
              <option key={month} value={month}>{month}</option>
            ))}
          </select>
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1a237e] focus:border-[#1a237e] outline-none"
            data-testid="year-filter"
          >
            {years.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          <div className="text-sm text-gray-600 flex items-center">
            {filteredSalaries.length} salary records found
          </div>
        </div>
      </div>

      {/* Salary Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Employee</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Department</th>
                <th className="text-left px-6 py-3 text-sm font-semibold text-gray-900">Month/Year</th>
                {isAdmin && (
                  <>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-gray-900">Gross</th>
                    <th className="text-right px-6 py-3 text-sm font-semibold text-gray-900">Deductions</th>
                  </>
                )}
                <th className="text-right px-6 py-3 text-sm font-semibold text-gray-900">Net Salary</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-gray-900">Status</th>
                <th className="text-center px-6 py-3 text-sm font-semibold text-gray-900">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filteredSalaries.map((salary) => (
                <tr key={salary.id} className="hover:bg-gray-50" data-testid={`salary-row-${salary.id}`}>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium text-gray-900">{salary.employeeName}</p>
                      <p className="text-sm text-gray-500">{salary.employeeId}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-gray-600">{salary.department}</td>
                  <td className="px-6 py-4 text-gray-600">{salary.month} {salary.year}</td>
                  {isAdmin && (
                    <>
                      <td className="px-6 py-4 text-right font-medium text-gray-900">
                        {formatCurrency(salary.grossSalary)}
                      </td>
                      <td className="px-6 py-4 text-right text-red-600">
                        -{formatCurrency(salary.totalDeductions)}
                      </td>
                    </>
                  )}
                  <td className="px-6 py-4 text-right font-bold text-green-600">
                    {formatCurrency(salary.netSalary)}
                  </td>
                  <td className="px-6 py-4 text-center">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      salary.status === 'Paid' 
                        ? 'bg-green-100 text-green-700' 
                        : 'bg-yellow-100 text-yellow-700'
                    }`}>
                      {salary.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-center">
                    <button
                      onClick={() => { setSelectedSalary(salary); setShowDetailModal(true); }}
                      className="p-2 text-[#1a237e] hover:bg-[#1a237e]/10 rounded-lg"
                      data-testid={`view-salary-${salary.id}`}
                    >
                      <Eye size={18} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filteredSalaries.length === 0 && (
          <div className="text-center py-12">
            <DollarSign className="mx-auto text-gray-400 mb-3" size={48} />
            <p className="text-gray-500">No salary records found for the selected period</p>
          </div>
        )}
      </div>

      {/* Salary Detail Modal */}
      {showDetailModal && selectedSalary && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg">
            <div className="bg-gradient-to-r from-[#1a237e] to-[#283593] p-6 text-white flex justify-between items-center rounded-t-2xl">
              <div>
                <h2 className="text-lg font-bold">Salary Slip</h2>
                <p className="text-white/70 text-sm">{selectedSalary.month} {selectedSalary.year}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="p-2 hover:bg-white/20 rounded-lg">
                <X size={20} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div className="text-center pb-4 border-b border-gray-200">
                <p className="font-semibold text-lg text-gray-900">{selectedSalary.employeeName}</p>
                <p className="text-gray-600">{selectedSalary.department}</p>
                <p className="text-sm text-gray-500">ID: {selectedSalary.employeeId}</p>
              </div>

              {/* Earnings */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Earnings</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Basic Salary</span>
                    <span className="font-medium">{formatCurrency(selectedSalary.basic)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Dearness Allowance (DA)</span>
                    <span className="font-medium">{formatCurrency(selectedSalary.da)}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">House Rent Allowance (HRA)</span>
                    <span className="font-medium">{formatCurrency(selectedSalary.hra)}</span>
                  </div>
                  {selectedSalary.conveyance > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Conveyance</span>
                      <span className="font-medium">{formatCurrency(selectedSalary.conveyance)}</span>
                    </div>
                  )}
                  {selectedSalary.bonus > 0 && (
                    <div className="flex justify-between text-green-600">
                      <span>Bonus</span>
                      <span className="font-medium">{formatCurrency(selectedSalary.bonus)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold pt-2 border-t border-gray-100">
                    <span>Gross Salary</span>
                    <span className="text-[#1a237e]">{formatCurrency(selectedSalary.grossSalary)}</span>
                  </div>
                </div>
              </div>

              {/* Deductions */}
              <div>
                <h4 className="font-semibold text-gray-900 mb-2">Deductions</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Provident Fund (PF)</span>
                    <span className="text-red-600">-{formatCurrency(selectedSalary.pfEmployee)}</span>
                  </div>
                  {selectedSalary.esi > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">ESI</span>
                      <span className="text-red-600">-{formatCurrency(selectedSalary.esi)}</span>
                    </div>
                  )}
                  {selectedSalary.professionalTax > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Professional Tax</span>
                      <span className="text-red-600">-{formatCurrency(selectedSalary.professionalTax)}</span>
                    </div>
                  )}
                  {selectedSalary.tds > 0 && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">TDS</span>
                      <span className="text-red-600">-{formatCurrency(selectedSalary.tds)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-semibold pt-2 border-t border-gray-100">
                    <span>Total Deductions</span>
                    <span className="text-red-600">-{formatCurrency(selectedSalary.totalDeductions)}</span>
                  </div>
                </div>
              </div>

              {/* Net Pay */}
              <div className="bg-green-50 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-lg font-semibold text-gray-900">Net Pay</span>
                  <span className="text-2xl font-bold text-green-600">{formatCurrency(selectedSalary.netSalary)}</span>
                </div>
              </div>

              {selectedSalary.paidDate && (
                <div className="text-center text-sm text-gray-500">
                  Paid on: {selectedSalary.paidDate}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SalaryDetails;
